// Every operation the connectors ATTEMPTED, on every platform, Skool included.
//
// The rest of this page answers "how much outreach landed". This answers "what
// did the system actually do" — including the failures, the cancellations and
// the queue, which is where the answer usually is when something looks broken.
//
// Three views of one ledger:
//   • output per day, split by what it was (DMs, comments, likes, invites,
//     connections, posts), with a platform lens
//   • the same totals per platform and per brand
//   • a cascade — brand → platform → day → the actual messages, with a link to
//     each profile or community
//
// Items are fetched lazily, only when a day is opened. The ledger is tens of
// thousands of rows; loading it to render a collapsed tree would make the page
// unusable to save one request.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { get } from "api/index";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

// One colour per category, used identically in every chart so a colour means
// the same thing wherever it appears.
export const CATEGORY_COLORS = {
  dm: "#2563eb",
  comment: "#7c3aed",
  like: "#db2777",
  invite: "#0891b2",
  connection: "#16a34a",
  post: "#ea580c",
};
export const CATEGORY_LABELS = {
  dm: "DMs",
  comment: "Comments",
  like: "Likes",
  invite: "Invites",
  connection: "Connections",
  post: "Posts",
};
const CATEGORIES = Object.keys(CATEGORY_COLORS);

const STATUS_COLORS = {
  success: "#16a34a", failed: "#dc2626", queued: "#f59e0b",
  cancelled: "#94a3b8", running: "#0891b2", held: "#7c3aed",
};

export function statusPill(status) {
  return {
    display: "inline-block", padding: "1px 7px", borderRadius: 10, fontSize: 11,
    fontWeight: 700, color: "#fff", background: STATUS_COLORS[status] || "#64748b",
  };
}

// "2026-08-11" → "11 Aug". Charts get unreadable with full ISO dates.
export function shortDay(day) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day || ""));
  if (!m) return String(day || "");
  const d = new Date(`${day}T00:00:00Z`);
  return `${Number(m[3])} ${d.toLocaleString("en", { month: "short", timeZone: "UTC" })}`;
}

export function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 16).replace("T", " ") + " UTC";
}

function Kpi({ label, value, tone }) {
  return (
    <div style={{
      flex: "1 1 120px", minWidth: 120, background: "#fff", borderRadius: 8, padding: "10px 12px",
      border: "1px solid #e5e7eb", borderLeft: `4px solid ${tone || "#94a3b8"}`,
    }}>
      <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>{value}</div>
    </div>
  );
}

function StackedBars({ data, xKey, title, testId, height = 260 }) {
  const present = CATEGORIES.filter((c) => data.some((row) => (row[c] || 0) > 0));
  if (!data.length) {
    return (
      <div data-testid={testId} style={{ padding: 16, color: "#94a3b8", fontSize: 13 }}>
        {title} — nothing in this window.
      </div>
    );
  }
  return (
    <div data-testid={testId} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 8px 4px" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", padding: "0 8px 8px" }}>{title}</div>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
          <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
          <Tooltip formatter={(v, n) => [v, CATEGORY_LABELS[n] || n]} />
          <Legend formatter={(n) => CATEGORY_LABELS[n] || n} wrapperStyle={{ fontSize: 12 }} />
          {present.map((c) => (
            <Bar key={c} dataKey={c} stackId="ops" name={c} fill={CATEGORY_COLORS[c]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// A row in the cascade's leaf: one operation, what it said, and where to see it.
function OperationRow({ item }) {
  return (
    <div data-testid="operation-row" style={{ padding: "8px 10px", borderTop: "1px solid #f1f5f9", fontSize: 12 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
        <span style={statusPill(item.status)}>{item.status}</span>
        <b style={{ color: "#0f172a" }}>{CATEGORY_LABELS[item.category] || item.op}</b>
        <span style={{ color: "#64748b" }}>{item.op}</span>
        <span style={{ color: "#94a3b8" }}>{fmtTime(item.at)}</span>
        {item.targetName ? <span style={{ color: "#334155" }}>→ {item.targetName}</span> : null}
        {item.profileUrl ? (
          <a href={item.profileUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb" }}>profile</a>
        ) : null}
        {item.postUrl ? (
          <a href={item.postUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb" }}>post</a>
        ) : null}
      </div>
      {item.text ? (
        <div style={{ marginTop: 4, color: "#0f172a", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{item.text}</div>
      ) : null}
      {item.error ? (
        <div style={{ marginTop: 4, color: "#b91c1c" }}>{item.error}</div>
      ) : null}
    </div>
  );
}

// brand → platform → day → items. Each level shows its own totals before it is
// opened, so the shape of the activity is readable without any clicking.
function DayNode({ days: windowDays, brandId, platform, node }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await get(
        `/community-analytics/operations/items?days=${windowDays}&brandId=${encodeURIComponent(brandId)}`
        + `&platform=${encodeURIComponent(platform)}&day=${encodeURIComponent(node.day)}&limit=200`,
        false,
      );
      setItems((r && r.items) || []);
    } catch (_) {
      setItems([]);            // a failed fetch reads as "none", never as a spinner forever
    } finally {
      setLoading(false);
    }
  }, [windowDays, brandId, platform, node.day]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && items === null && !loading) load();
  };

  const counts = Object.entries(node.ops || {}).filter(([, n]) => n > 0);
  return (
    <div style={{ marginLeft: 18 }}>
      <button
        type="button"
        onClick={toggle}
        data-testid="ops-day"
        style={{ background: "none", border: 0, padding: "5px 0", cursor: "pointer", textAlign: "left", width: "100%", fontSize: 12 }}
      >
        <span style={{ color: "#64748b" }}>{open ? "▾" : "▸"}</span>{" "}
        <b style={{ color: "#0f172a" }}>{shortDay(node.day)}</b>{" "}
        <span style={{ color: "#64748b" }}>{node.attempted} attempted</span>
        {node.failed ? <span style={{ color: "#b91c1c" }}> · {node.failed} failed</span> : null}
        {node.queued ? <span style={{ color: "#b45309" }}> · {node.queued} queued</span> : null}
        <span style={{ color: "#94a3b8" }}>
          {counts.length ? ` · ${counts.map(([c, n]) => `${n} ${CATEGORY_LABELS[c] || c}`).join(", ")}` : ""}
        </span>
      </button>
      {open ? (
        <div style={{ marginLeft: 14, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6 }}>
          {loading ? <div style={{ padding: 10, fontSize: 12, color: "#94a3b8" }}>Loading…</div> : null}
          {!loading && items && items.length === 0 ? (
            <div style={{ padding: 10, fontSize: 12, color: "#94a3b8" }}>No operations recorded.</div>
          ) : null}
          {!loading && items ? items.map((it) => <OperationRow key={it.id} item={it} />) : null}
        </div>
      ) : null}
    </div>
  );
}

function PlatformNode({ days, brandId, node }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginLeft: 16 }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="ops-platform"
        style={{ background: "none", border: 0, padding: "5px 0", cursor: "pointer", textAlign: "left", width: "100%", fontSize: 13 }}
      >
        <span style={{ color: "#64748b" }}>{open ? "▾" : "▸"}</span>{" "}
        <b style={{ color: "#0f172a", textTransform: "capitalize" }}>{node.platform}</b>{" "}
        <span style={{ color: "#64748b" }}>{node.attempted} attempted · {node.success} ok</span>
        {node.failed ? <span style={{ color: "#b91c1c" }}> · {node.failed} failed</span> : null}
      </button>
      {open ? node.days.map((d) => (
        <DayNode key={d.day} days={days} brandId={brandId} platform={node.platform} node={d} />
      )) : null}
    </div>
  );
}

function BrandNode({ days, node }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderTop: "1px solid #e5e7eb", padding: "6px 0" }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="ops-brand"
        style={{ background: "none", border: 0, padding: "6px 0", cursor: "pointer", textAlign: "left", width: "100%", fontSize: 14 }}
      >
        <span style={{ color: "#64748b" }}>{open ? "▾" : "▸"}</span>{" "}
        <b style={{ color: "#0f172a" }}>{node.brandName || node.brandId}</b>{" "}
        <span style={{ color: "#64748b" }}>{node.attempted} attempted</span>
        {node.failed ? <span style={{ color: "#b91c1c" }}> · {node.failed} failed</span> : null}
        {node.queued ? <span style={{ color: "#b45309" }}> · {node.queued} queued</span> : null}
      </button>
      {open ? node.platforms.map((p) => (
        <PlatformNode key={p.platform} days={days} brandId={node.brandId} node={p} />
      )) : null}
    </div>
  );
}

const OperationsReport = ({ days = 30 }) => {
  const [summary, setSummary] = useState(null);
  const [tree, setTree] = useState(null);
  const [platform, setPlatform] = useState("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [s, t] = await Promise.all([
          get(`/community-analytics/operations/summary?days=${days}&platform=${encodeURIComponent(platform)}`, false),
          get(`/community-analytics/operations/tree?days=${days}`, false),
        ]);
        if (!alive) return;
        setSummary(s || null);
        setTree(t || null);
      } catch (_) {
        if (alive) { setSummary(null); setTree(null); }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [days, platform]);

  const platforms = useMemo(
    () => (summary && summary.byPlatform ? summary.byPlatform.map((p) => p.platform) : []),
    [summary],
  );
  const totals = (summary && summary.totals) || {};
  const byDay = (summary && summary.byDay) || [];
  const byPlatform = (summary && summary.byPlatform) || [];
  const byBrand = (summary && summary.byBrand) || [];

  return (
    <div data-testid="operations-report" style={{ marginTop: 28 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, fontSize: 17, color: "#0f172a" }}>All attempted operations</h3>
        <span style={{ fontSize: 12, color: "#64748b" }}>
          every platform including Skool — successes, failures, cancellations and what is still queued
        </span>
        <select
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
          data-testid="ops-platform-filter"
          style={{ marginLeft: "auto", fontSize: 12, padding: "4px 8px", borderRadius: 6, border: "1px solid #d1d5db" }}
        >
          <option value="all">All platforms</option>
          {platforms.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {loading ? <div style={{ padding: 12, fontSize: 13, color: "#94a3b8" }}>Loading…</div> : null}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "12px 0" }}>
        <Kpi label="Attempted" value={totals.attempted || 0} tone="#0f172a" />
        <Kpi label="Succeeded" value={totals.success || 0} tone={STATUS_COLORS.success} />
        <Kpi label="Failed" value={totals.failed || 0} tone={STATUS_COLORS.failed} />
        <Kpi label="Queued" value={totals.queued || 0} tone={STATUS_COLORS.queued} />
        <Kpi label="Held" value={totals.held || 0} tone={STATUS_COLORS.held} />
        <Kpi label="Cancelled" value={totals.cancelled || 0} tone={STATUS_COLORS.cancelled} />
      </div>

      <StackedBars
        testId="ops-chart-day"
        title="Output per day"
        xKey="dayLabel"
        data={byDay.map((d) => ({ ...d, dayLabel: shortDay(d.day) }))}
      />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12, marginTop: 12 }}>
        <StackedBars testId="ops-chart-platform" title="Per platform" xKey="platform" data={byPlatform} height={220} />
        <StackedBars
          testId="ops-chart-brand"
          title="Per brand"
          xKey="brandLabel"
          height={220}
          data={byBrand.map((b) => ({ ...b, brandLabel: b.brandName || b.brandId }))}
        />
      </div>

      <div style={{ marginTop: 18 }}>
        <h4 style={{ margin: "0 0 4px", fontSize: 15, color: "#0f172a" }}>Brand → platform → day → what was sent</h4>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>
          Open a day to read the actual messages, comments and invites, with a link to each profile.
        </div>
        <div data-testid="ops-cascade" style={{ background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 8, padding: "4px 12px" }}>
          {tree && tree.brands && tree.brands.length
            ? tree.brands.map((b) => <BrandNode key={b.brandId} days={days} node={b} />)
            : <div style={{ padding: 12, fontSize: 13, color: "#94a3b8" }}>No operations in this window.</div>}
        </div>
      </div>
    </div>
  );
};

export default OperationsReport;
